import React, { useCallback, useEffect, useRef, useState } from "react";
import "./style.scss";

interface Props {
  onRecorded: (blob: Blob, duration: number) => void;
  onCancel: () => void;
  maxDuration?: number;
}

function VideoRecorder({ onRecorded, onCancel, maxDuration = 60 }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCamera = useCallback(async () => {
    try {
      if (!navigator.mediaDevices) {
        console.error("Camera access failed: mediaDevices not available (requires HTTPS or localhost)");
        onCancel();
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 480, height: 480 },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera access failed:", err);
      onCancel();
    }
  }, [onCancel]);

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startCamera]);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mr = new MediaRecorder(streamRef.current, { mimeType: "video/webm" });
    mr.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    mr.onstop = () => {
      const duration = (Date.now() - startTimeRef.current) / 1000;
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      onRecorded(blob, Math.round(duration));
    };
    mediaRecorderRef.current = mr;
    mr.start();
    startTimeRef.current = Date.now();
    setRecording(true);
    setElapsed(0);
    timerRef.current = setInterval(() => {
      const s = Math.round((Date.now() - startTimeRef.current) / 1000);
      setElapsed(s);
      if (s >= maxDuration) {
        mr.stop();
        setRecording(false);
        if (timerRef.current) clearInterval(timerRef.current);
      }
    }, 500);
  }, [onRecorded, maxDuration]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
  }, [recording]);

  const handleCancel = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (timerRef.current) clearInterval(timerRef.current);
    onCancel();
  }, [onCancel]);

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="video-recorder">
      <div className="video-recorder__preview">
        <video ref={videoRef} autoPlay playsInline muted className="video-recorder__video" />
        {recording && (
          <div className="video-recorder__timer">
            <span className="video-recorder__rec-dot" />
            {formatTime(elapsed)}
          </div>
        )}
      </div>
      <div className="video-recorder__controls">
        <button type="button" className="video-recorder__cancel-btn" onClick={handleCancel}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
          </svg>
        </button>
        {!recording ? (
          <button type="button" className="video-recorder__record-btn" onClick={startRecording}>
            <span className="video-recorder__record-circle" />
          </button>
        ) : (
          <button type="button" className="video-recorder__stop-btn" onClick={stopRecording}>
            <span className="video-recorder__stop-square" />
          </button>
        )}
      </div>
    </div>
  );
}

export default VideoRecorder;
